/**
 * Users and roles.
 *
 * Adding somebody is an **allowlist entry**, not an account: no password, no Clerk user. The
 * Clerk invitation is a separate manual step with its own lead time, which is why people waiting
 * for one are listed apart from people who have signed in — "waiting for their invitation" and
 * "having trouble signing in" are different problems.
 *
 * Deactivate is the revocation path that sticks: the pipeline checks it *before* the allowlist
 * re-seed, so a deactivated person is not silently re-granted on their next request. Revoke does
 * both in one operation.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ErrorNote, PageHeader } from '@/components/bits';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { ROLE_LABEL } from '@/lib/policy';
import { formatInstant } from '@/lib/dates';
import type { AdminUser } from '@/lib/types';

const ASSIGNABLE = ['staff', 'dept_manager', 'dept_head', 'admin', 'superadmin'] as const;

export function HrUsers() {
  const session = useSession();
  const cache = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('dept_manager');
  const [confirming, setConfirming] = useState<{
    user: AdminUser;
    action: 'deactivate' | 'revoke';
  } | null>(null);
  const [reason, setReason] = useState('');

  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers });
  const audit = useQuery({ queryKey: ['admin-audit'], queryFn: () => api.auditTrail(50) });

  const invalidate = async () => {
    await cache.invalidateQueries({ queryKey: ['admin-users'] });
    await cache.invalidateQueries({ queryKey: ['admin-audit'] });
  };

  const add = useMutation({
    mutationFn: () => api.addUser({ email: email.trim(), role }),
    onSuccess: async () => {
      setAdding(false);
      setEmail('');
      await invalidate();
    },
  });

  const setRoleFor = useMutation({
    mutationFn: (input: { id: number; role: string }) => api.setUserRole(input.id, input.role),
    onSuccess: invalidate,
  });

  const act = useMutation({
    mutationFn: async () => {
      if (!confirming) throw new Error('nothing to confirm');
      return confirming.action === 'revoke'
        ? api.revokeUser(confirming.user.id, reason.trim())
        : api.deactivateUser(confirming.user.id, reason.trim());
    },
    onSuccess: async () => {
      setConfirming(null);
      setReason('');
      await invalidate();
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: number) => api.reactivateUser(id),
    onSuccess: invalidate,
  });

  const rows = users.data?.users ?? [];
  const pending = users.data?.pending ?? [];

  return (
    <>
      <PageHeader
        title="Users and roles"
        subtitle="Access is granted here. Signing in does not grant it."
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            Grant access
          </Button>
        }
      />

      {pending.length > 0 ? (
        <Card title={`${pending.length} allowlisted, never signed in`} className="mb-6">
          <p className="text-muted-foreground mb-3 text-sm">
            The app-side grant is waiting for them. What is missing is a Clerk invitation, which is
            a manual step outside this app.
          </p>
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Role they will get</Th>
                <Th>Added by</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {pending.map((entry) => (
                <tr key={entry.email}>
                  <Td>{entry.email}</Td>
                  <Td className="text-xs">
                    {ROLE_LABEL[entry.intended_role] ?? entry.intended_role}
                  </Td>
                  <Td className="text-xs">{entry.source}</Td>
                  <Td className="whitespace-nowrap text-xs">{formatInstant(entry.added_at)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}

      <Card title={`${rows.length} with an account`} className="mb-6">
        {users.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <EmptyState title="Nobody has signed in yet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Last seen</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <tr key={user.id}>
                  <Td>
                    {user.email}
                    {user.is_owner ? (
                      <span className="ml-2">
                        <Badge tone="good" title="Protected — cannot be demoted or deactivated">
                          owner
                        </Badge>
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    <Select
                      aria-label={`Role for ${user.email}`}
                      className="h-8 w-48 py-1 text-xs"
                      value={user.role ?? 'staff'}
                      disabled={
                        user.is_owner ||
                        user.id === session.me.user.id ||
                        setRoleFor.isPending ||
                        !session.me.capabilities.manage_users
                      }
                      onChange={(event) =>
                        setRoleFor.mutate({ id: user.id, role: event.target.value })
                      }
                    >
                      {ASSIGNABLE.map((value) => (
                        <option key={value} value={value}>
                          {ROLE_LABEL[value] ?? value}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">{formatInstant(user.last_seen_at)}</Td>
                  <Td>
                    {user.deactivated ? (
                      <Badge tone="bad">deactivated</Badge>
                    ) : (
                      <Badge tone="good">active</Badge>
                    )}
                  </Td>
                  <Td>
                    <div className="flex gap-2">
                      {user.deactivated ? (
                        <Button
                          size="sm"
                          busy={reactivate.isPending}
                          onClick={() => reactivate.mutate(user.id)}
                        >
                          Reactivate
                        </Button>
                      ) : user.is_owner || user.id === session.me.user.id ? null : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              setReason('');
                              setConfirming({ user, action: 'deactivate' });
                            }}
                          >
                            Deactivate
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setReason('');
                              setConfirming({ user, action: 'revoke' });
                            }}
                          >
                            Revoke
                          </Button>
                        </>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {setRoleFor.error ? <ErrorNote error={setRoleFor.error} /> : null}
        {reactivate.error ? <ErrorNote error={reactivate.error} /> : null}
      </Card>

      <Card title="Recent changes">
        {(audit.data?.events ?? []).length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing recorded yet.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>What</Th>
                <Th>Subject</Th>
                <Th>By</Th>
              </tr>
            </thead>
            <tbody>
              {(audit.data?.events ?? []).map((event) => {
                const row = event as Record<string, string | number | null>;
                return (
                  <tr key={String(row.id)}>
                    <Td className="whitespace-nowrap text-xs">
                      {formatInstant(String(row.created_at))}
                    </Td>
                    <Td>{String(row.action).replace(/_/g, ' ')}</Td>
                    <Td className="text-xs">
                      {String(row.subject_type)} #{String(row.subject_id)}
                    </Td>
                    <Td className="text-xs">
                      {row.actor_user_id === null
                        ? String(row.actor_source)
                        : `user #${row.actor_user_id}`}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Grant access"
        footer={
          <>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              variant="primary"
              busy={add.isPending}
              disabled={email.trim() === ''}
              onClick={() => add.mutate()}
            >
              Add to the allowlist
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          This creates an allowlist entry, not an account. If they have never signed in, invite the
          same address in Clerk — that step is manual and has a lead time.
        </p>
        <Field label="Email address" required>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              placeholder="someone@leychoon.com"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label="Role" hint="You cannot grant a role above your own.">
          {(props) => (
            <Select {...props} value={role} onChange={(event) => setRole(event.target.value)}>
              {ASSIGNABLE.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABEL[value] ?? value}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {add.error ? <ErrorNote error={add.error} /> : null}
      </Dialog>

      <Dialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming?.action === 'revoke' ? 'Revoke access' : 'Deactivate'}
        footer={
          <>
            <Button onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              variant="destructive"
              busy={act.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => act.mutate()}
            >
              {confirming?.action === 'revoke' ? 'Revoke' : 'Deactivate'}
            </Button>
          </>
        }
      >
        <Alert tone="warn">
          {confirming?.action === 'revoke' ? (
            <>
              Revoking deactivates <strong>{confirming?.user.email}</strong> and deletes their
              allowlist entry, in one operation. Use this when someone should not come back.
            </>
          ) : (
            <>
              Deactivating stops <strong>{confirming?.user.email}</strong> immediately. The
              allowlist entry stays, so reactivating is one click — but the deactivation is checked
              before the allowlist is re-read, which is what makes it stick.
            </>
          )}
        </Alert>
        <Field label="Reason" required hint="Recorded in the audit trail.">
          {(props) => (
            <Input {...props} value={reason} onChange={(event) => setReason(event.target.value)} />
          )}
        </Field>
        {act.error ? <ErrorNote error={act.error} /> : null}
      </Dialog>
    </>
  );
}
